import {
  Directive,
  ElementRef,
  Input,
  OnDestroy,
  OnInit,
  Renderer2
} from '@angular/core'

/**
 * @description
 * Directive for lazy loading images using Intersection Observer API.
 * Only loads images when they enter the viewport, improving performance
 * when displaying many images.
 *
 * @example
 * ```html
 * <img vdrLazyLoadImage [lazySrc]="imageUrl" [lazyPlaceholder]="placeholderUrl" />
 * ```
 */
@Directive({
  selector: 'img[vdrLazyLoadImage]'
})
export class LazyLoadImageDirective implements OnInit, OnDestroy {
  @Input() lazySrc: string
  @Input() lazyPlaceholder: string =
    'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"%3E%3Crect fill="%23f0f0f0" width="100" height="100"/%3E%3C/svg%3E'

  private intersectionObserver?: IntersectionObserver
  private isLoaded = false

  constructor(
    private el: ElementRef<HTMLImageElement>,
    private renderer: Renderer2
  ) {}

  ngOnInit() {
    // Set placeholder immediately
    this.renderer.setAttribute(
      this.el.nativeElement,
      'src',
      this.lazyPlaceholder
    )
    this.renderer.addClass(this.el.nativeElement, 'lazy-loading')

    // Check if IntersectionObserver is supported
    if ('IntersectionObserver' in window) {
      this.setupIntersectionObserver()
    } else {
      // Fallback: load image immediately if IntersectionObserver not supported
      this.loadImage()
    }
  }

  ngOnDestroy() {
    if (this.intersectionObserver) {
      this.intersectionObserver.disconnect()
    }
  }

  private setupIntersectionObserver() {
    const options: IntersectionObserverInit = {
      root: null,
      rootMargin: '50px', // Start loading 50px before entering viewport
      threshold: 0.01
    }

    this.intersectionObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting && !this.isLoaded) {
          this.loadImage()
          // Stop observing after loading
          if (this.intersectionObserver) {
            this.intersectionObserver.unobserve(this.el.nativeElement)
          }
        }
      })
    }, options)

    this.intersectionObserver.observe(this.el.nativeElement)
  }

  private loadImage() {
    if (this.isLoaded || !this.lazySrc) {
      return
    }

    const img = new Image()
    img.onload = () => {
      this.renderer.setAttribute(this.el.nativeElement, 'src', this.lazySrc)
      this.renderer.removeClass(this.el.nativeElement, 'lazy-loading')
      this.renderer.addClass(this.el.nativeElement, 'lazy-loaded')
      this.isLoaded = true
    }

    img.onerror = () => {
      this.renderer.removeClass(this.el.nativeElement, 'lazy-loading')
      this.renderer.addClass(this.el.nativeElement, 'lazy-error')
      this.isLoaded = true
    }

    img.src = this.lazySrc
  }
}
